import { type InjectedFormikProps, withFormik } from "formik";

import yup from "utilities/form/yup";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTForm, BTFormItem } from "commonComponents/btWrappers/BTForm/BTForm";
import { BTIconCloseOutlined } from "commonComponents/btWrappers/BTIcon";
import { BTSelect } from "commonComponents/btWrappers/BTSelect/BTSelect";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import {
    type GlobalSearchFilters,
    type IGlobalSearchFilterValues,
} from "commonComponents/utilities/MainNavigation/globalSearch/GlobalSearch.types";
import { AllJobs } from "commonComponents/utilities/MainNavigation/searchLegacy/SearchBar.api.types";

import "./GlobalSearchAllFilters.less";

interface IGlobalSearchAllFiltersProps {
    filters: GlobalSearchFilters | undefined;
    selectedFilters: IGlobalSearchFilterValues;
    onFilterChange: (filters: IGlobalSearchFilterValues) => void;
    onClose: (filters: IGlobalSearchFilterValues | null) => void;
}

export interface ISearchFiltersFormValues {
    globalSearchJobId: number;
    globalSearchCategory: number[];
}

const GlobalSearchAllFiltersInternal: React.FunctionComponent<
    InjectedFormikProps<IGlobalSearchAllFiltersProps, IGlobalSearchFilterValues>
> = ({ setFieldValue, values, onFilterChange, onClose, filters, setFieldTouched, resetForm }) => {
    if (!filters) {
        return null;
    }

    const dropDownWrapper = (menu: React.ReactElement) => {
        return <div className="GlobalSearch">{menu}</div>;
    };

    return (
        <div className="GlobalSearchAllFilters">
            <div className="flex">
                <BTTitle className="Header" level={2}>
                    Filters
                </BTTitle>
                <div className="flex justify-content-end">
                    <BTButtonDeprecated
                        data-testid="close"
                        type="link"
                        onClick={() => onClose(null)}
                        className="FilterCloseButton"
                        icon={<BTIconCloseOutlined />}
                    />
                </div>
            </div>
            <BTForm>
                <BTFormItem label="Job" className="SelectFilter">
                    <BTSelect
                        className="GlobalFilters"
                        id="globalSearchJobId"
                        treeData={filters.jobSelectItems}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        value={values.globalSearchJobId}
                        data-testid="jobSelect"
                        dropdownRender={dropDownWrapper}
                    />
                </BTFormItem>

                <BTFormItem label="Category" className="SelectFilter">
                    <BTSelect
                        id="globalSearchCategory"
                        data-testid="categorySelect"
                        treeData={filters.categorySelectItems}
                        onChange={setFieldValue}
                        value={values.globalSearchCategory}
                        onBlur={setFieldTouched}
                        dropdownRender={dropDownWrapper}
                        multiple
                    />
                </BTFormItem>
                <div className="FilterButtons">
                    <BTButtonDeprecated
                        id="btnResetFilters"
                        data-testid="btnResetFilters"
                        type="secondary"
                        onClick={() => {
                            resetForm();
                            onFilterChange({
                                globalSearchCategory: [],
                                globalSearchJobId: AllJobs,
                            });
                        }}
                    >
                        Reset Filters
                    </BTButtonDeprecated>
                    <BTButtonDeprecated
                        id="btnApplyFilters"
                        data-testid="btnApplyFilters"
                        type="primary"
                        onClick={() => onClose(values)}
                    >
                        Search
                    </BTButtonDeprecated>
                </div>
            </BTForm>
        </div>
    );
};

const SearchFiltersValidators = yup.object().shape<ISearchFiltersFormValues>({
    globalSearchJobId: yup.number(),
    globalSearchCategory: yup.array(),
});

export const GlobalSearchAllFilters = withFormik<
    IGlobalSearchAllFiltersProps,
    ISearchFiltersFormValues
>({
    mapPropsToValues: (props: IGlobalSearchAllFiltersProps) => ({
        globalSearchJobId: props.selectedFilters.globalSearchJobId,
        globalSearchCategory: props.selectedFilters.globalSearchCategory,
    }),
    enableReinitialize: true,
    handleSubmit: () => {},
    validationSchema: SearchFiltersValidators,
})(GlobalSearchAllFiltersInternal);
